// Lead suchen und wählen (Zusammenführen, Datenschutz › Auskunft und Löschen). Sucht über die Befehlspalette-Suche (`/api/search`) nach
// Name, Telefonnummer und E-Mail-Adresse; Treffer auf Kontakte führen zum Lead. Tastatur: Pfeile, Enter wählt, Escape leert.
import { useEffect, useRef, useState } from "react";
import { sucheApi } from "../api";
import type { SuchTreffer } from "../api/types";
import { fehlerInfo } from "../components/Fehler";
import { StatusPille } from "../components/Bausteine";

export interface GewaehlterLead {
  leadId: string;
  name: string;
  statusLabel: string | null;
}

interface Zeile extends GewaehlterLead {
  untertitel: string | null;
  grund: string | null;
}

const GRUND: Record<SuchTreffer["match"], string | null> = { name: null, company: null, phone: "Nummer", email: "E-Mail" };

function zeilenAus(treffer: SuchTreffer[], ausschliessen: string[]): Zeile[] {
  const gesehen = new Set<string>(ausschliessen);
  const zeilen: Zeile[] = [];
  for (const t of treffer) {
    if (gesehen.has(t.lead_id)) continue;
    gesehen.add(t.lead_id);
    const istKontakt = t.type === "contact";
    zeilen.push({
      leadId: t.lead_id,
      name: istKontakt ? (t.subtitle ?? t.title) : t.title,
      statusLabel: t.status_label,
      untertitel: istKontakt ? `Kontakt: ${t.title}${t.match_value ? ` · ${t.match_value}` : ""}` : t.subtitle,
      grund: istKontakt ? GRUND[t.match] : null,
    });
  }
  return zeilen;
}

export function LeadWahl({ onWahl, ausschliessen = [], platzhalter = "Lead suchen …", autoFokus = false }: {
  onWahl: (lead: GewaehlterLead) => void;
  ausschliessen?: string[];
  platzhalter?: string;
  autoFokus?: boolean;
}) {
  const [q, setQ] = useState("");
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [aktiv, setAktiv] = useState(0);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const aus = useRef(ausschliessen.join("|"));
  aus.current = ausschliessen.join("|");

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) {
      setZeilen([]);
      setFehler(null);
      return;
    }
    const abbruch = new AbortController();
    const t = window.setTimeout(async () => {
      setLaedt(true);
      try {
        const r = await sucheApi.suchen(text, 15, abbruch.signal);
        setZeilen(zeilenAus(r.data, aus.current ? aus.current.split("|") : []).slice(0, 8));
        setAktiv(0);
        setFehler(null);
      } catch (e) {
        if ((e as Error).name !== "AbortError") setFehler(fehlerInfo(e).text);
      } finally {
        setLaedt(false);
      }
    }, 220);
    return () => {
      window.clearTimeout(t);
      abbruch.abort();
    };
  }, [q]);

  const waehle = (z: Zeile) => onWahl({ leadId: z.leadId, name: z.name, statusLabel: z.statusLabel });

  return (
    <div className="dub-wahl">
      <input
        className="eingabe"
        type="search"
        placeholder={platzhalter}
        value={q}
        autoFocus={autoFokus}
        aria-label={platzhalter}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && zeilen.length) {
            e.preventDefault();
            setAktiv((a) => Math.min(zeilen.length - 1, a + 1));
          } else if (e.key === "ArrowUp" && zeilen.length) {
            e.preventDefault();
            setAktiv((a) => Math.max(0, a - 1));
          } else if (e.key === "Enter" && zeilen[aktiv]) {
            e.preventDefault();
            waehle(zeilen[aktiv]);
          } else if (e.key === "Escape" && q) {
            e.stopPropagation();
            setQ("");
          }
        }}
      />
      {fehler ? <div className="hinweis hinweis--fehler">{fehler}</div> : null}
      {zeilen.length ? (
        <ul className="dub-wahl__liste" role="listbox">
          {zeilen.map((z, i) => (
            <li key={z.leadId} role="option" aria-selected={i === aktiv}>
              <button type="button" className={`dub-wahl__zeile${i === aktiv ? " dub-wahl__zeile--aktiv" : ""}`} onMouseEnter={() => setAktiv(i)} onClick={() => waehle(z)}>
                <span className="dub-wahl__name">{z.name}</span>
                {z.statusLabel ? <StatusPille label={z.statusLabel} klein schlicht /> : null}
                {z.untertitel ? <span className="leise dub-wahl__unter">{z.untertitel}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : q.trim().length >= 2 && !laedt && !fehler ? (
        <div className="leise dub-wahl__leer">Kein Lead gefunden.</div>
      ) : null}
    </div>
  );
}
